import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3,
  FileText,
  TrendingUp,
  TrendingDown,
  DollarSign,
  RefreshCw,
  AlertCircle,
  SlidersHorizontal,
  LayoutList,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import InlineDatePicker from '../components/InlineDatePicker';
import reportService from '../services/reportService';
import cashBoxService, { CashBox } from '../services/cashBoxService';
import { DailyBalanceDTO, ReportMovementDTO } from '../types';
import { showError } from '../components/Alerts';

// ==================== HELPERS ====================

const getDefaultDates = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last  = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    start: first.toISOString().split('T')[0],
    end:   last.toISOString().split('T')[0],
  };
};

const fmtMoney = (amount: number, symbol: string) =>
  `${symbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('es-AR', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

const fmtShort = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });

// ==================== COMPONENTE PRINCIPAL ====================

export default function Reports() {
  const defaults = getDefaultDates();

  const [cashBoxes, setCashBoxes]         = useState<CashBox[]>([]);
  const [boxLoading, setBoxLoading]       = useState(true);
  const [selectedBoxId, setSelectedBoxId] = useState<number | null>(null);
  const [startDate, setStartDate]         = useState(defaults.start);
  const [endDate, setEndDate]             = useState(defaults.end);

  const [summaryData, setSummaryData]       = useState<DailyBalanceDTO[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError]     = useState<string | null>(null);

  const [movData, setMovData]       = useState<ReportMovementDTO[]>([]);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError]     = useState<string | null>(null);

  // Cargar cajas al montar
  useEffect(() => {
    cashBoxService.getAllCashBoxes()
      .then((boxes) => {
        setCashBoxes(boxes);
        if (boxes.length > 0) setSelectedBoxId(boxes[0].id);
      })
      .catch(() => showError('Error al cargar las cajas'))
      .finally(() => setBoxLoading(false));
  }, []);

  // Cargar datos cuando cambien los filtros
  useEffect(() => {
    if (!selectedBoxId || !startDate || !endDate) return;

    setSummaryLoading(true);
    setSummaryError(null);
    reportService
      .getDailyBalance({ cashBoxId: selectedBoxId, startDate, endDate })
      .then(setSummaryData)
      .catch((err: any) => {
        const msg = err?.response?.data?.message || 'Error al cargar el resumen.';
        setSummaryError(msg);
        showError(msg);
      })
      .finally(() => setSummaryLoading(false));

    setMovLoading(true);
    setMovError(null);
    reportService
      .getCashBoxMovements({ cashBoxId: selectedBoxId, startDate, endDate })
      .then(setMovData)
      .catch((err: any) => {
        const msg = err?.response?.data?.message || 'Error al cargar los movimientos.';
        setMovError(msg);
      })
      .finally(() => setMovLoading(false));
  }, [selectedBoxId, startDate, endDate]);

  const selectedBox = cashBoxes.find((b) => b.id === selectedBoxId);
  const currency    = selectedBox?.currency ?? 'ARS';
  const symbol      = currency === 'USD' ? 'US$' : '$';
  const fmt         = (n: number) => fmtMoney(n, symbol);
  const isRefreshing = summaryLoading || movLoading;

  const totals = useMemo(() =>
    summaryData.reduce(
      (acc, d) => ({
        income:  acc.income  + d.totalCreditos,
        expense: acc.expense + d.totalDebitos,
        net:     acc.net     + d.netChange,
      }),
      { income: 0, expense: 0, net: 0 },
    ), [summaryData]);

  const maxAmount = useMemo(() =>
    Math.max(...summaryData.map((d) => Math.max(d.totalCreditos, d.totalDebitos, 1)), 1),
  [summaryData]);

  const handleRefresh = () => {
    if (!selectedBoxId || !startDate || !endDate) return;
    setSummaryError(null);
    setMovError(null);
    setSummaryLoading(true);
    setMovLoading(true);
    reportService
      .getDailyBalance({ cashBoxId: selectedBoxId, startDate, endDate })
      .then(setSummaryData)
      .catch((err: any) => setSummaryError(err?.response?.data?.message || 'Error'))
      .finally(() => setSummaryLoading(false));
    reportService
      .getCashBoxMovements({ cashBoxId: selectedBoxId, startDate, endDate })
      .then(setMovData)
      .catch((err: any) => setMovError(err?.response?.data?.message || 'Error'))
      .finally(() => setMovLoading(false));
  };

  // ==================== RENDER ====================

  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Reportes</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Análisis contable y movimientos de cajas
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {selectedBox && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  <BarChart3 className="w-3.5 h-3.5" />
                  {selectedBox.name}
                  <Badge variant="outline" className="ml-1 px-1.5 py-0 h-4 text-[10px] border-primary/30">
                    {currency}
                  </Badge>
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing || !selectedBoxId}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>

        {/* ── Cuerpo ── */}
        <div className="p-4 space-y-6 sm:p-6">

          {/* ── Filtros (patrón 3 columnas) ── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                Filtros de Consulta
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Seleccioná una caja y el período a analizar.
              </p>
            </div>

            <div className="space-y-4 lg:col-span-2">
              {/* Selector de caja */}
              <div className="space-y-1.5">
                <Label htmlFor="caja-select" className="text-xs font-medium text-foreground sm:text-sm">
                  Caja
                </Label>
                {boxLoading ? (
                  <div className="h-10 flex items-center px-3 text-sm text-muted-foreground border rounded-lg border-border bg-background">
                    <RefreshCw className="w-4 h-4 animate-spin mr-2 text-muted-foreground" />
                    Cargando cajas...
                  </div>
                ) : cashBoxes.length === 0 ? (
                  <div className="h-10 flex items-center px-3 text-sm text-muted-foreground border rounded-lg border-border bg-background">
                    Sin cajas disponibles
                  </div>
                ) : (
                  <Select
                    value={selectedBoxId ? String(selectedBoxId) : ''}
                    onValueChange={(v) => setSelectedBoxId(Number(v))}
                  >
                    <SelectTrigger id="caja-select" className="rounded-lg bg-background border-border">
                      <SelectValue placeholder="Seleccioná una caja" />
                    </SelectTrigger>
                    <SelectContent>
                      {cashBoxes.map((box) => (
                        <SelectItem key={box.id} value={String(box.id)}>
                          {box.name}
                          <span className="ml-2 text-[10px] text-muted-foreground font-medium">
                            {box.currency}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Rango de fechas */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InlineDatePicker
                  id="reporte-desde"
                  label="Desde"
                  value={startDate}
                  onChange={setStartDate}
                />
                <InlineDatePicker
                  id="reporte-hasta"
                  label="Hasta"
                  value={endDate}
                  onChange={setEndDate}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-border" />

          {/* ── Tabs ── */}
          <Tabs defaultValue="summary">
            <TabsList>
              <TabsTrigger value="summary" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Resumen Diario
              </TabsTrigger>
              <TabsTrigger value="movements" className="flex items-center gap-2">
                <LayoutList className="w-4 h-4" />
                Movimientos
              </TabsTrigger>
            </TabsList>

            {/* ─ Resumen Diario ─ */}
            <TabsContent value="summary" className="mt-5 max-h-none overflow-visible space-y-5">
              {summaryLoading ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Calculando resumen...</p>
                </div>
              ) : summaryError ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/20 text-destructive text-sm">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  {summaryError}
                </div>
              ) : summaryData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center border border-border rounded-xl bg-background">
                  <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-8 h-8 text-primary/40" />
                  </div>
                  <h3 className="mb-1 text-base font-semibold text-foreground">Sin datos para este período</h3>
                  <p className="text-sm text-muted-foreground">Ajustá el rango de fechas o seleccioná otra caja.</p>
                </div>
              ) : (
                <>
                  {/* KPIs */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background">
                      <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="w-4 h-4 text-success" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Total Ingresos</p>
                        <p className="text-base font-bold text-success tabular-nums">+{fmt(totals.income)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background">
                      <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <TrendingDown className="w-4 h-4 text-destructive" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Total Egresos</p>
                        <p className="text-base font-bold text-destructive tabular-nums">-{fmt(totals.expense)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${totals.net >= 0 ? 'bg-info/10' : 'bg-destructive/10'}`}>
                        <DollarSign className={`w-4 h-4 ${totals.net >= 0 ? 'text-info' : 'text-destructive'}`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Resultado Neto</p>
                        <p className={`text-base font-bold tabular-nums ${totals.net >= 0 ? 'text-info' : 'text-destructive'}`}>
                          {totals.net >= 0 ? '+' : ''}{fmt(totals.net)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Chart + Tabla */}
                  <div className="border border-border rounded-xl overflow-hidden">
                    {/* Gráfico */}
                    <div className="px-5 pt-5 pb-4 bg-muted/30 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                        Rendimiento Diario
                      </p>
                      <div className="flex items-end gap-1 h-20">
                        {summaryData.map((day, i) => {
                          const incH = (day.totalCreditos / maxAmount) * 100;
                          const expH = (day.totalDebitos  / maxAmount) * 100;
                          return (
                            <div
                              key={i}
                              className="flex-1 flex items-end gap-[1px] group min-w-0"
                              title={`${fmtDate(day.date)}\nIngreso: ${fmt(day.totalCreditos)} | Egreso: ${fmt(day.totalDebitos)}`}
                            >
                              <div
                                className="flex-1 rounded-t-sm bg-success/50 group-hover:bg-success/70 transition-colors min-h-[3px]"
                                style={{ height: `${Math.max(incH, 4)}%` }}
                              />
                              <div
                                className="flex-1 rounded-t-sm bg-destructive/50 group-hover:bg-destructive/70 transition-colors min-h-[3px]"
                                style={{ height: `${Math.max(expH, 4)}%` }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-muted-foreground">{fmtShort(summaryData[0].date)}</span>
                        <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-sm bg-success/50 inline-block" />Ingresos
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-sm bg-destructive/50 inline-block" />Egresos
                          </span>
                        </div>
                        {summaryData.length > 1 && (
                          <span className="text-[10px] text-muted-foreground">{fmtShort(summaryData[summaryData.length - 1].date)}</span>
                        )}
                      </div>
                    </div>

                    {/* Tabla */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/20">
                            <TableHead className="py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fecha</TableHead>
                            <TableHead className="py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ingresos</TableHead>
                            <TableHead className="py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Egresos</TableHead>
                            <TableHead className="py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Neto del Día</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {summaryData.map((day, i) => (
                            <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                              <TableCell className="py-3 font-medium text-foreground">{fmtDate(day.date)}</TableCell>
                              <TableCell className="py-3 text-right text-success font-medium tabular-nums">
                                {day.totalCreditos > 0 ? `+${fmt(day.totalCreditos)}` : fmt(0)}
                              </TableCell>
                              <TableCell className="py-3 text-right text-destructive font-medium tabular-nums">
                                {day.totalDebitos > 0 ? `-${fmt(day.totalDebitos)}` : fmt(0)}
                              </TableCell>
                              <TableCell className="py-3 text-right">
                                <Badge
                                  variant={day.netChange >= 0 ? 'default' : 'destructive'}
                                  className={`tabular-nums font-semibold px-2 ${
                                    day.netChange >= 0
                                      ? 'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
                                      : ''
                                  }`}
                                >
                                  {day.netChange >= 0 ? '+' : ''}{fmt(day.netChange)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ─ Movimientos ─ */}
            <TabsContent value="movements" className="mt-5 max-h-none overflow-visible space-y-4">
              {movLoading ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Cargando movimientos...</p>
                </div>
              ) : movError ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/20 text-destructive text-sm">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  {movError}
                </div>
              ) : movData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center border border-border rounded-xl bg-background">
                  <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-primary/40" />
                  </div>
                  <h3 className="mb-1 text-base font-semibold text-foreground">Sin movimientos en este período</h3>
                  <p className="text-sm text-muted-foreground">No hay registros para el rango seleccionado.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{movData.length}</span>{' '}
                      movimiento{movData.length !== 1 ? 's' : ''} encontrado{movData.length !== 1 ? 's' : ''}
                    </p>
                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground bg-muted/30">
                      Vista de Auditoría
                    </Badge>
                  </div>

                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/20">
                            <TableHead className="py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fecha</TableHead>
                            <TableHead className="py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Descripción</TableHead>
                            <TableHead className="py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tipo</TableHead>
                            <TableHead className="py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Monto</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {movData.map((mov) => {
                            const isCredito  = mov.entryType === 'CREDITO';
                            const parsedDate = new Date(mov.date);
                            const showTime   = mov.date.includes('T') && !mov.date.endsWith('T00:00:00');
                            return (
                              <TableRow key={mov.id} className="hover:bg-muted/30 transition-colors">
                                <TableCell className="py-3 align-top whitespace-nowrap">
                                  <p className="text-sm text-foreground">
                                    {parsedDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </p>
                                  {showTime && (
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                      {parsedDate.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="py-3 align-top">
                                  <p className="text-sm text-foreground">{mov.description || 'Movimiento de caja'}</p>
                                  {mov.transactionId && (
                                    <p className="text-[10px] text-muted-foreground uppercase mt-0.5">
                                      Ref: TXN-{mov.transactionId}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="py-3 align-top whitespace-nowrap">
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] uppercase font-medium ${
                                      isCredito
                                        ? 'border-success/30 text-success bg-success/5'
                                        : 'border-destructive/30 text-destructive bg-destructive/5'
                                    }`}
                                  >
                                    {mov.transactionType || mov.entryType}
                                  </Badge>
                                </TableCell>
                                <TableCell className="py-3 align-top text-right">
                                  <span className={`text-sm font-bold tabular-nums ${isCredito ? 'text-success' : 'text-destructive'}`}>
                                    {isCredito ? '+' : '-'}{fmt(mov.amount)}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
